'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import GfBento from '@/components/common/GfBento'
import GfSectionMark from '@/components/common/GfSectionMark'
import { t } from '@/i18n/translations'

/**
 * "Gallery · Field" attractions LIST for a city.
 *
 * Two interactive blocks ported from the Достопримечательности reference:
 *   1. PLACES — search box + type filter chips + `.gf-arts`/`.ar` cards.
 *   2. COMPARE — a `.gf-plans` ledger (place / city / type) with a `gf-plan-count`
 *      label and sortable `.act` headers.
 *
 * `places` is pre-mapped on the server to
 *   { id, name, href, blurb, type, typeValue, city, img, alt }.
 * `types` is [{ value, label }] (only types that have places).
 * Card markup mirrors GfCitiesGrid / GfArticleCards.
 */
export default function PlacesListContent({
    places = [],
    types = [],
    lang = 'en',
    countryName = '',
}) {
    const ui = t(lang, 'toursWidgetsUi').places

    // ── PLACES grid: type chip + free-text search ──
    const [type, setType] = useState('all')
    const [query, setQuery] = useState('')

    // ── COMPARE ledger: independent type filter + sortable columns ──
    const [ledgerType, setLedgerType] = useState('all')
    const [sortKey, setSortKey] = useState('name')
    const [sortDir, setSortDir] = useState(1)

    const shown = useMemo(() => {
        const q = query.trim().toLowerCase()
        return places.filter((p) => {
            const okType = type === 'all' || p.typeValue === type
            const okQ =
                !q ||
                (p.name || '').toLowerCase().includes(q) ||
                (p.blurb || '').toLowerCase().includes(q) ||
                (p.city || '').toLowerCase().includes(q) ||
                (p.type || '').toLowerCase().includes(q)
            return okType && okQ
        })
    }, [places, type, query])

    const ledgerRows = useMemo(() => {
        const filtered = places.filter(
            (p) => ledgerType === 'all' || p.typeValue === ledgerType
        )
        const dir = sortDir
        const get = (r) => (sortKey === 'type' ? r.type || '' : r.name || '')
        return filtered.slice().sort((a, b) => {
            const x = get(a)
            const y = get(b)
            if (x === y) return 0
            return (x < y ? -1 : 1) * dir
        })
    }, [places, ledgerType, sortKey, sortDir])

    const onSort = (key) => {
        if (sortKey === key) setSortDir((d) => d * -1)
        else {
            setSortKey(key)
            setSortDir(1)
        }
    }

    const arrow = (key) => (sortKey === key ? (sortDir > 0 ? '▲' : '▼') : '')

    return (
        <>
            {/* ── 01 · PLACES ──────────────────────────────────── */}
            <div className="cs-search">
                <svg
                    className="ic"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={ui.search}
                    aria-label={ui.searchAria}
                />
            </div>

            {types.length > 1 && (
                <div className="gf-ops" role="group" aria-label={ui.typesAria}>
                    <button
                        type="button"
                        className={`all ${type === 'all' ? 'on' : ''}`}
                        onClick={() => setType('all')}
                    >
                        {ui.all}
                    </button>
                    {types.map((tp) => (
                        <button
                            type="button"
                            key={tp.value}
                            className={type === tp.value ? 'on' : ''}
                            onClick={() => setType(tp.value)}
                        >
                            {tp.label}
                        </button>
                    ))}
                </div>
            )}

            {shown.length > 0
                ? <GfBento items={shown.map((p) => ({
                    id: p.id,
                    href: p.href,
                    img: p.img,
                    srcSet: p.srcSet,
                    alt: p.alt || p.name,
                    cat: p.type,
                    title: p.name,
                    excerpt: p.blurb,
                    meta: p.city,
                }))} />
                : <div className="cs-empty">{ui.empty}</div>}

            {/* ── COMPARE ledger ───────────────────────────────── */}
            {places.length > 1 && (
                <section className="gf-sec" id="compare" style={{ paddingTop: 0 }}>
                    <GfSectionMark
                        eyebrow={ui.compareEyebrow}
                        tag={(ledgerType === 'all'
                            ? ui.allTypes
                            : types.find((tp) => tp.value === ledgerType)?.label || ledgerType) +
                            (countryName ? ` · ${countryName}` : '')}
                        title={ui.compareTitle}
                    />

                    {types.length > 1 && (
                        <div className="gf-ops" role="group" aria-label={ui.typeAria}>
                            <button
                                type="button"
                                className={`all ${ledgerType === 'all' ? 'on' : ''}`}
                                onClick={() => setLedgerType('all')}
                            >
                                {ui.allTypes}
                            </button>
                            {types.map((tp) => (
                                <button
                                    type="button"
                                    key={tp.value}
                                    className={ledgerType === tp.value ? 'on' : ''}
                                    onClick={() => setLedgerType(tp.value)}
                                >
                                    {tp.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="gf-plan-head">
                        <span className="gf-plan-count">
                            {ledgerRows.length} {ui.placePlural(ledgerRows.length)}
                        </span>
                        <span className="gf-note">
                            {ledgerType === 'all'
                                ? ui.allAttractionTypes
                                : ui.typeLabel(types.find((tp) => tp.value === ledgerType)?.label || ledgerType)}
                        </span>
                    </div>

                    <div className="gf-plans gf-plans-c2">
                        <div className="gf-plan-row head">
                            <button
                                type="button"
                                data-sort="name"
                                className={sortKey === 'name' ? 'act' : ''}
                                onClick={() => onSort('name')}
                                aria-label={ui.sortBy(ui.place)}
                            >
                                {ui.place}
                                <i className="ar" aria-hidden="true">{arrow('name')}</i>
                            </button>
                            <button
                                type="button"
                                data-sort="type"
                                className={sortKey === 'type' ? 'act' : ''}
                                onClick={() => onSort('type')}
                                aria-label={ui.sortBy(ui.type)}
                            >
                                {ui.type}
                                <i className="ar" aria-hidden="true">{arrow('type')}</i>
                            </button>
                            {/* invisible clone of the row's guide link so the header's last
                                (auto) track reserves the same width — keeps columns aligned */}
                            <span className="sel" aria-hidden="true" style={{ visibility: 'hidden' }}>{ui.guide}</span>
                        </div>
                        {ledgerRows.map((r) => (
                            <div className="gf-plan-row" key={r.id}>
                                <span className="d">
                                    <span className="gbline">{r.name}</span>
                                </span>
                                <span className="per">{r.type || '—'}</span>
                                {r.href ? (
                                    <Link className="sel" href={r.href}>
                                        {ui.guide}
                                    </Link>
                                ) : (
                                    <span className="sel" style={{ opacity: 0.4, cursor: 'default' }}>
                                        {ui.guide}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </>
    )
}
